import Category from "../../entities/category";

/**
 * Category actions
 *
 * Call order:
 *
 * CATEGORY_SAVE_QUEUE:	adds the item to the save queue
 * --> CATEGORY_SAVE:	modifies the state
 * --> CATEGORY_SAVE_SUCCESS: if all went well
 * --> CATEGORY_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail CATEGORY_SAVE_QUEUE is during the call to CATEGORY_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at CATEGORY_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: CATEGORY_SAVE_SUCCESS, CATEGORY_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or CATEGORY_PENDING_CHECK (it left the queue).
 */

export const CATEGORY_FETCH_ONE_QUEUE: any = 'CATEGORY_FETCH_ONE_QUEUE';
export const CATEGORY_FETCH_SUCCESS: any = 'CATEGORY_FETCH_SUCCESS';
export const CATEGORY_FETCH_FAILURE: any = 'CATEGORY_FETCH_FAILURE';
export const CATEGORY_FETCH_ONE_SUCCESS: any = 'CATEGORY_FETCH_ONE_SUCCESS';
export const CATEGORY_FETCH_ONE_FAILURE: any = 'CATEGORY_FETCH_ONE_FAILURE';

export const CATEGORY_SAVE: any = 'CATEGORY_SAVE';
export const CATEGORY_SAVE_QUEUE: any = 'CATEGORY_SAVE_QUEUE';
export const CATEGORY_SAVE_SUCCESS: any = 'CATEGORY_SAVE_SUCCESS';
export const CATEGORY_SAVE_FAILURE: any = 'CATEGORY_SAVE_FAILURE';

export const CATEGORY_SAVE_MANY: any = 'CATEGORY_SAVE_MANY';
export const CATEGORY_SAVE_MANY_QUEUE: any = 'CATEGORY_SAVE_MANY_QUEUE';
export const CATEGORY_SAVE_MANY_SUCCESS: any = 'CATEGORY_SAVE_MANY_SUCCESS';
export const CATEGORY_SAVE_MANY_FAILURE: any = 'CATEGORY_SAVE_MANY_FAILURE';

export const CATEGORY_REMOVE: any = 'CATEGORY_REMOVE';
export const CATEGORY_REMOVE_QUEUE: any = 'CATEGORY_REMOVE_QUEUE';
export const CATEGORY_REMOVE_SUCCESS: any = 'CATEGORY_REMOVE_SUCCESS';
export const CATEGORY_REMOVE_FAILURE: any = 'CATEGORY_REMOVE_FAILURE';

export const CATEGORY_RESTORE: any = 'CATEGORY_RESTORE';
export const CATEGORY_RESTORE_QUEUE: any = 'CATEGORY_RESTORE_QUEUE';
export const CATEGORY_RESTORE_SUCCESS: any = 'CATEGORY_RESTORE_SUCCESS';
export const CATEGORY_RESTORE_FAILURE: any = 'CATEGORY_RESTORE_FAILURE';

export const CATEGORY_RESTORE_MANY: any = 'CATEGORY_RESTORE_MANY';
export const CATEGORY_REMOVE_MANY: any = 'CATEGORY_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const CATEGORY_SAVE_REVERT: any = 'CATEGORY_SAVE_REVERT';
export const CATEGORY_PENDING_CHECK: any = 'CATEGORY_PENDING_CHECK';

/* Fetch actions */
export const fetchCategorySuccess = (categories: Category[], partial: boolean, lastFetch: number) => ({type: CATEGORY_FETCH_SUCCESS, categories, partial, lastFetch});
export const fetchCategoryFailure = (err: any) => {
		console.error('CategoryAction: fetchCategoryFailure', err);
		return {type: CATEGORY_FETCH_FAILURE, err};
};
export const fetchOneCategoryQueue = (categoryId: string, sentAddedOn?: number) => ({type: CATEGORY_FETCH_ONE_QUEUE, categoryId, sentAddedOn});
export const fetchOneCategorySuccess = (category: Category | null, sentAddedOn?: number, categoryId: string = category?.id) => ({type: CATEGORY_FETCH_ONE_SUCCESS, categoryId, category, sentAddedOn});
export const fetchOneCategoryFailure = (id: string, err: any) => {
		console.error('CategoryAction: fetchOneCategoryFailure', err);
		return {type: CATEGORY_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveCategory = (category: Category) => {
    // console.debug('⮂ CATEGORY_SAVE action', category?.id);
    return {type: CATEGORY_SAVE, category};
}
export const saveCategoryQueue = (category: Category, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CATEGORY_SAVE_QUEUE action', category?.id);
    return {type: CATEGORY_SAVE_QUEUE, category, onQueued};
}
export const saveCategorySuccess = (category: Category, sentAddedOn?: number) => {
    // console.debug('⮂ CATEGORY_SAVE_SUCCESS action', category?.id);
    return {type: CATEGORY_SAVE_SUCCESS, category, sentAddedOn};
}
export const saveCategoryFailure = (category: Category, status: number, message: string) => {
    console.warn(`⮂ CATEGORY_SAVE_FAILURE error saving category: HTTP ${status}: ${message}`, category?.id);
    return {type: CATEGORY_SAVE_FAILURE, category, status, message};
};

/* SaveMany actions */
export const saveManyCategories = (categories: Category[]) => {
    // console.debug('⮂ CATEGORY_SAVE_MANY action', categories?.map(category => category?.id).join(','));
    return {type: CATEGORY_SAVE_MANY, categories};
}
export const saveManyCategoriesQueue = (categories: Category[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ CATEGORY_SAVE_MANY_QUEUE action', categories?.map(category => category.id).join(','));
    return {type: CATEGORY_SAVE_MANY_QUEUE, categories, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `categories` (OWA-1018) */
export const saveManyCategoriesSuccess = (categories: Category[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ CATEGORY_SAVE_MANY_SUCCESS action', categories?.map(category => category.id).join(','));
    return {type: CATEGORY_SAVE_MANY_SUCCESS, categories, sentAddedOns};
}
export const saveManyCategoriesFailure = (categories: Category[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_CATEGORIES_FAILURE Error saving categories: HTTP ${status}: ${message}`, categories?.map(category => category.id).join(','));
		return {type: CATEGORY_SAVE_MANY_FAILURE, categories, status, message};
};

/* Remove actions */
export const removeCategory = (category: Category) => ({ type: CATEGORY_REMOVE, category });
export const removeCategoryQueue = (category: Category) => ({ type: CATEGORY_REMOVE_QUEUE, category });
export const removeCategorySuccess = (category: Category) => ({ type: CATEGORY_REMOVE_SUCCESS, category });
export const removeCategoryFailure = (category: Category, status: number, message: string) => {
	console.error(`Redux: removeCategoryFailure Error removing category: HTTP ${status}: ${message}`);
	return ({ type: CATEGORY_REMOVE_FAILURE, category, status, message });
};

/* Restore actions */
export const restoreCategory = (category: Category) => ({ type: CATEGORY_RESTORE, category });
export const restoreCategoryQueue = (category: Category) => ({ type: CATEGORY_RESTORE_QUEUE, category });
export const restoreCategorySuccess = (category: Category, sentAddedOn?: number) => ({ type: CATEGORY_RESTORE_SUCCESS, category, sentAddedOn });
export const restoreCategoryFailure = (category: Category, status: number, message: string) => {
	console.error(`Redux: restoreCategoryFailure Error restoring category: HTTP ${status}: ${message}`);
	return ({ type: CATEGORY_RESTORE_FAILURE, category, status, message });
};

/* RestoreMany actions */
export const restoreManyCategories = (categories: Category[]) => ({ type: CATEGORY_RESTORE_MANY, categories });

/* RemoveMany actions */
export const removeManyCategories = (categories: Category[]) => ({ type: CATEGORY_REMOVE_MANY, categories });

/* Pending save actions (OWA-1018) */
/**
 * The save `category` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertCategorySave = (category: Category, options: {sentAddedOn?: number, current?: Category | null, keep?: boolean} = {}) =>
    ({type: CATEGORY_SAVE_REVERT, category, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkCategoryPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: CATEGORY_PENDING_CHECK, live, refused, readAt});